import React, { useMemo } from "react"

import { useHumanReviewProjectsTransport } from "@/human-feedback/transport"
import { Button } from "@coval/ui"
import { ClipboardCheck } from "lucide-react"

import { FilterPopover } from "@/components/filters/FilterPopover"
import { isFilterSectionDisabled } from "@/components/filters/filterSectionVisibility"
import { FilterTrigger } from "@/components/filters/FilterTrigger"
import { SearchableCheckboxList } from "@/components/filters/SearchableCheckboxList"

import { useConversationsFilter } from "../../contexts/ConversationsFilterContext"
import type { ConversationsFilterSectionProps } from "../../types/filters"

/**
 * Human review project multi-select section of the Conversations filter menu.
 * Selecting projects keeps only the conversations linked to any of them.
 */
export const ProjectsFilterSection: React.FC<
  ConversationsFilterSectionProps
> = ({ isOpen, onToggle, filterEnabled = true }) => {
  const {
    state: { projectIds },
    setProjectIds,
  } = useConversationsFilter()
  const {
    data: projects,
    isError,
    isLoading,
    refetch,
  } = useHumanReviewProjectsTransport({ enabled: filterEnabled })

  const items = useMemo(
    () =>
      (projects ?? []).map((project) => ({
        id: project.ulid,
        label: project.name,
        searchValue: project.name,
      })),
    [projects],
  )

  const isDisabled = isFilterSectionDisabled({
    activeFilterCount: projectIds.length,
    isDeferred: !filterEnabled,
    isError,
    isLoading,
    optionCount: items.length,
  })

  return (
    <FilterPopover
      isOpen={isOpen}
      onToggle={onToggle}
      width="w-72"
      disabled={isDisabled}
      emptyMessage="No human review projects in this workspace."
      trigger={
        <FilterTrigger
          label="Projects"
          icon={<ClipboardCheck className="w-4 h-4" />}
          isActive={projectIds.length > 0}
          disabled={isDisabled}
        />
      }
    >
      {isError && (
        <div role="alert" className="p-3 text-sm text-coval-danger-text">
          Could not load projects.{" "}
          <Button variant="link" onClick={() => void refetch()}>
            Retry projects
          </Button>
        </div>
      )}
      <SearchableCheckboxList
        items={items}
        selectedIds={projectIds}
        onSelectionChange={setProjectIds}
        isLoading={isLoading}
        isDeferred={!filterEnabled}
        isOpen={isOpen}
        placeholder="Search projects..."
        emptyMessage="No projects found"
        noItemsMessage={isError ? undefined : "No projects available"}
        width="w-72"
        renderItem={(item) => (
          <span className="block min-w-0 truncate text-sm">{item.label}</span>
        )}
      />
    </FilterPopover>
  )
}
